// In-chat opportunity card — pure helpers shared by the chat state (use-chat), the BFF
// history mapping (messages API), and the card UI (MessageRow/OpportunityCard). The wire
// shape is the harness contract: the ASSISTANT reply of a turn whose SuggestOpportunity
// tool ran carries `metadata.opportunityCard` (and the SSE `messageEnd` event carries the
// same card in-band, like `choices`). The card's two CTAs are the reply's ordinary
// `choices` chips, minted from that same tool call — nothing here renders them.
//
// Deliberately dependency-free (mirrors lib/connector-card.ts): unit tests must not drag
// the common-lib graph, so the validation here MIRRORS common-lib's `readOpportunityCard`
// rather than importing it. Keep the two in step.

/**
 * The value families an opportunity can be tagged with — which KIND of gain it is. The
 * maestro picks exactly one; the id drives the chip's colour and rise arrow, and the words
 * come from i18n (`chat.opportunityCard.chip.<family>`).
 */
export const CHIP_FAMILIES = [
  'revenue',
  'new-clients',
  'retention',
  'client-results',
  'get-paid',
  'staffing',
  'time-saved',
  'reputation',
  'launch',
  'wellbeing',
  'habits',
  'learning',
  'relationships',
  'money',
  'in-control',
  'experiences',
] as const;

export type ChipFamily = (typeof CHIP_FAMILIES)[number];

export type OpportunityCardData = {
  /** Goal slug — the handle the CTAs resolve back to. */
  slug: string;
  /** The win: what the business gets, benefit-framed. Model-authored, already localized. */
  title: string;
  /** The value family. Absent → the card renders with no chip. */
  value?: ChipFamily;
  /** One line naming the mechanism. Model-authored, already localized. */
  how?: string;
};

/**
 * Read + sanitize `metadata.opportunityCard` off a raw message entity.
 *
 * `slug` and `title` are required: a card whose CTAs cannot resolve to a goal, or that has
 * no win to show, renders nothing rather than a broken card — the reply's own words already
 * made the suggestion. An unrecognized `value` drops just the chip, since a forward family
 * from a newer server must not blank the whole card.
 */
export function readOpportunityCard(metadata: unknown): OpportunityCardData | undefined {
  const raw = (metadata as { opportunityCard?: unknown } | undefined)?.opportunityCard;
  if (!raw || typeof raw !== 'object') return undefined;
  const { slug, title, value, how } = raw as { slug?: unknown; title?: unknown; value?: unknown; how?: unknown };
  if (typeof slug !== 'string' || !slug.trim() || typeof title !== 'string' || !title.trim()) {
    return undefined;
  }
  const family = typeof value === 'string' && (CHIP_FAMILIES as readonly string[]).includes(value) ? (value as ChipFamily) : undefined;
  return {
    slug: slug.trim(),
    title: title.trim(),
    ...(family ? { value: family } : {}),
    ...(typeof how === 'string' && how.trim() ? { how: how.trim() } : {}),
  };
}

/**
 * The chip's look, per family: its colour and whether it carries the rise arrow. Derived from
 * the id in code and never from the localized label, so a translation can never change the
 * visual — and the arrow never has to be translated in 37 locales.
 *
 * The three families the taxonomy marks with "↑" are the ones whose whole point is a number
 * going up. The rest name a kind of gain that an arrow would overstate.
 */
export const CHIP_STYLE: Record<ChipFamily, { hue: string; rises?: true }> = {
  // ONE mapping across web, iOS and Android — the native pair holds the same sixteen against its own
  // token names, so a family's colour is the family's colour on every surface. The hex beside each
  // entry is what native resolves to; where web's palette has no exact match the nearest accent is
  // used and noted, because a family reading as roughly the same colour everywhere matters more than
  // an exact hex that only one surface can hit.
  //
  // NOTE: these sixteen assignments were chosen by us, not by design — nothing in the repo or the
  // spec assigns colours to families. They want a design pass before launch.
  revenue: { hue: 'var(--ds-accent-09)', rises: true }, // #276454
  'new-clients': { hue: 'var(--ds-accent-07)' }, // #3B6BE1
  retention: { hue: 'var(--ds-accent-06)', rises: true }, // #9A75FF ~ native #B06BD6
  'client-results': { hue: 'var(--ds-accent-14)' }, // #9CD5C6
  'get-paid': { hue: 'var(--ds-accent-04)' }, // #D6FF59
  staffing: { hue: 'var(--ds-accent-02)' }, // #EDCBFF ~ native #D4BFFF
  'time-saved': { hue: 'var(--ds-accent-08)' }, // #96F4FF
  reputation: { hue: 'var(--ds-accent-17)', rises: true }, // #FFC738 ~ native #B26A00
  launch: { hue: 'var(--ds-accent-11)' }, // #FF773A
  wellbeing: { hue: 'var(--ds-accent-20)' }, // #8DC7B8 ~ native linkGreen
  habits: { hue: 'var(--ds-accent-16)' }, // #C3D3F7 ~ native #81B4FF
  learning: { hue: 'var(--ds-accent-13)' }, // #D2FBF2 ~ native #8FD9F0
  relationships: { hue: 'var(--ds-accent-12)' }, // #FF9FFC
  money: { hue: 'var(--ds-accent-10)' }, // #A4FF70
  'in-control': { hue: 'var(--ds-accent-05)' }, // #81252C ~ native #5D3565, the only deep tone here
  experiences: { hue: 'var(--ds-accent-03)' }, // #FF4E00 ~ native #FF7A00
};
